import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, CheckCircle, Moon, Star, TrendingUp, Calendar } from 'lucide-react';

interface KundliDetailsProps {
  data: any;
}

export function KundliDetails({ data }: KundliDetailsProps) {
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

  const getSignTraits = (sign: string) => {
    const traits: Record<string, string[]> = {
      'Aries': ['Energetic', 'Courageous', 'Confident', 'Impulsive'],
      'Taurus': ['Reliable', 'Patient', 'Practical', 'Stubborn'],
      'Gemini': ['Adaptable', 'Communicative', 'Witty', 'Inconsistent'],
      'Cancer': ['Emotional', 'Intuitive', 'Nurturing', 'Moody'],
      'Leo': ['Creative', 'Generous', 'Confident', 'Dominating'],
      'Virgo': ['Analytical', 'Practical', 'Reliable', 'Critical'],
      'Libra': ['Diplomatic', 'Fair', 'Social', 'Indecisive'],
      'Scorpio': ['Passionate', 'Resourceful', 'Brave', 'Jealous'],
      'Sagittarius': ['Optimistic', 'Adventurous', 'Honest', 'Tactless'],
      'Capricorn': ['Disciplined', 'Responsible', 'Ambitious', 'Pessimistic'],
      'Aquarius': ['Progressive', 'Independent', 'Humanitarian', 'Aloof'],
      'Pisces': ['Compassionate', 'Artistic', 'Intuitive', 'Escapist']
    };
    return traits[sign] || [];
  };

  const getPlanetSignificance = (planet: string) => {
    const significance: Record<string, string> = {
      'Sun': 'Soul, Father, Authority, Leadership',
      'Moon': 'Mind, Mother, Emotions, Peace',
      'Mars': 'Energy, Courage, Siblings, Property',
      'Mercury': 'Intelligence, Communication, Business',
      'Jupiter': 'Wisdom, Fortune, Children, Teachers',
      'Venus': 'Love, Marriage, Luxury, Arts',
      'Saturn': 'Discipline, Karma, Longevity, Obstacles'
    };
    return significance[planet] || 'Represents specific life aspects';
  };

  const getHouseSignificance = (houseNum: number) => {
    const significance: Record<number, string> = {
      1: 'Self, Personality, Physical Body',
      2: 'Wealth, Family, Speech',
      3: 'Siblings, Courage, Communication',
      4: 'Mother, Home, Property, Happiness',
      5: 'Children, Education, Intelligence',
      6: 'Enemies, Diseases, Debts, Service',
      7: 'Marriage, Partnership, Spouse',
      8: 'Longevity, Inheritance, Occult',
      9: 'Fortune, Father, Religion, Higher Learning',
      10: 'Career, Status, Fame, Authority',
      11: 'Gains, Friends, Wishes, Income',
      12: 'Losses, Expenses, Spirituality, Foreign'
    };
    return significance[houseNum] || '';
  };

  return (
    <div className="space-y-6">
      {/* Birth Details */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="size-5 text-orange-600" />
          Birth Details
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Name</div>
            <div className="font-semibold text-gray-900">{data.basicDetails.name}</div>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Gender</div>
            <div className="font-semibold text-gray-900 capitalize">{data.basicDetails.gender}</div>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Date of Birth</div>
            <div className="font-semibold text-gray-900">
              {new Date(data.basicDetails.dateOfBirth).toLocaleDateString('en-IN', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Time of Birth</div>
            <div className="font-semibold text-gray-900">{data.basicDetails.timeOfBirth}</div>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Place of Birth</div>
            <div className="font-semibold text-gray-900">{data.basicDetails.placeOfBirth}</div>
          </div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Coordinates</div>
            <div className="font-semibold text-gray-900">
              {parseFloat(data.basicDetails.latitude).toFixed(4)}°N, {parseFloat(data.basicDetails.longitude).toFixed(4)}°E
            </div>
          </div>
        </div>
      </Card>

      {/* Nakshatra Details */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Moon className="size-5 text-blue-600" />
          Nakshatra (Birth Star)
        </h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-white rounded-lg shadow">
              <div className="text-3xl font-bold text-blue-600">{data.nakshatras.name}</div>
              <div className="text-sm text-gray-600 mt-1">Pada: {data.nakshatras.pada}</div>
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-600">Nakshatra Lord</div>
              <div className="text-xl font-semibold text-gray-900">{data.nakshatras.lord}</div>
            </div>
          </div>
          <div className="p-4 bg-white rounded-lg">
            <div className="text-sm text-gray-600 mb-2">About Nakshatra</div>
            <p className="text-sm text-gray-700">
              {data.nakshatras.name} is one of the 27 Nakshatras in Vedic astrology. It plays a crucial role in determining personality traits, 
              marriage compatibility, and life events. The ruling planet {data.nakshatras.lord} influences your natural tendencies and spiritual path.
            </p>
          </div>
        </div>
      </Card>

      {/* Planetary Positions */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Star className="size-5 text-yellow-600" />
          Planetary Positions
        </h3>
        <div className="space-y-3">
          {data.planetaryPositions.map((planet: any, index: number) => (
            <div key={index} className="p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`text-3xl ${planet.color}`}>{planet.icon}</span>
                  <div>
                    <div className="font-semibold text-gray-900">{planet.name}</div>
                    <div className="text-sm text-gray-600">{getPlanetSignificance(planet.name)}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-orange-600">{planet.sign}</div>
                  <div className="text-sm text-gray-600">{planet.degree}° in House {planet.house}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Houses */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="size-5 text-purple-600" />
          House Details (Bhavas)
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {data.houses.map((house: any, index: number) => (
            <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-bold text-purple-600">House {house.house}</div>
                  <div className="text-sm text-gray-600">{getHouseSignificance(house.house)}</div>
                </div>
                <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                  {house.sign}
                </Badge>
              </div>
              {house.planets.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {house.planets.map((planetName: string, idx: number) => {
                    const planet = data.planetaryPositions.find((p: any) => p.name === planetName);
                    return (
                      <span
                        key={idx}
                        className={`inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-sm ${planet?.color || 'text-gray-700'}`}
                      >
                        {planet?.icon} {planetName}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Dosha Analysis */}
      <Card className="p-6 bg-white/80 backdrop-blur-sm shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="size-5 text-red-600" />
          Dosha Analysis
        </h3>
        
        <div className="space-y-4">
          {/* Manglik Dosha */}
          <div className="p-4 rounded-lg border" style={{
            backgroundColor: data.doshas.manglik ? '#fef2f2' : '#f0fdf4',
            borderColor: data.doshas.manglik ? '#fecaca' : '#bbf7d0'
          }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {data.doshas.manglik ? (
                  <AlertTriangle className="size-5 text-red-600" />
                ) : (
                  <CheckCircle className="size-5 text-green-600" />
                )}
                <span className="font-semibold text-gray-900">Manglik Dosha (Mars Affliction)</span>
              </div>
              <Badge 
                variant="secondary"
                className={data.doshas.manglik ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}
              >
                {data.doshas.manglik ? `Present - ${data.doshas.manglikIntensity}` : 'Not Present'}
              </Badge>
            </div>
            {data.doshas.manglik && (
              <div className="text-sm text-gray-700 mt-2 space-y-1">
                {data.doshas.details.map((detail: string, idx: number) => (
                  <div key={idx} className="pl-4 border-l-2 border-red-300">• {detail}</div>
                ))}
                <p className="mt-3 text-gray-600">
                  Manglik Dosha can be remedied through specific pujas, wearing gemstones, or marrying another Manglik person.
                </p>
              </div>
            )}
            {!data.doshas.manglik && (
              <p className="text-sm text-gray-600 mt-2">
                No Manglik Dosha detected. Mars is favorably placed in your chart.
              </p>
            )}
          </div>

          {/* Kaal Sarp Dosha */}
          <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="size-5 text-blue-600" />
                <span className="font-semibold text-gray-900">Kaal Sarp Dosha</span>
              </div>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                Not Present
              </Badge>
            </div>
            <p className="text-sm text-gray-600">
              All planets are favorably positioned. No Kaal Sarp Dosha detected in your chart.
            </p>
          </div>
        </div>
      </Card>

      {/* Personality Traits */}
      <Card className="p-6 bg-gradient-to-br from-orange-50 to-rose-50 border-orange-200 shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Personality Traits</h3>
        <div className="space-y-4">
          <div>
            <div className="font-semibold text-gray-900 mb-2">Sun Sign Traits ({data.predictions.sunSign})</div>
            <div className="flex flex-wrap gap-2">
              {getSignTraits(data.predictions.sunSign).map((trait, idx) => (
                <Badge key={idx} variant="secondary" className="bg-orange-100 text-orange-700">
                  {trait}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="font-semibold text-gray-900 mb-2">Moon Sign Traits ({data.predictions.moonSign})</div>
            <div className="flex flex-wrap gap-2">
              {getSignTraits(data.predictions.moonSign).map((trait, idx) => (
                <Badge key={idx} variant="secondary" className="bg-blue-100 text-blue-700">
                  {trait}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="font-semibold text-gray-900 mb-2">Ascendant Traits ({data.predictions.ascendant})</div>
            <div className="flex flex-wrap gap-2">
              {getSignTraits(data.predictions.ascendant).map((trait, idx) => (
                <Badge key={idx} variant="secondary" className="bg-purple-100 text-purple-700">
                  {trait}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Recommendations */}
      <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-xl">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Astrological Recommendations</h3>
        <div className="space-y-3">
          <div className="p-3 bg-white rounded-lg">
            <div className="font-semibold text-gray-900 mb-1">Lucky Day</div>
            <div className="text-sm text-gray-600">Based on your planetary positions, focus on activities on days ruled by your strong planets.</div>
          </div>
          <div className="p-3 bg-white rounded-lg">
            <div className="font-semibold text-gray-900 mb-1">Gemstone Recommendation</div>
            <div className="text-sm text-gray-600">Consult an astrologer for personalized gemstone recommendations based on your chart.</div>
          </div>
          <div className="p-3 bg-white rounded-lg">
            <div className="font-semibold text-gray-900 mb-1">Mantra</div>
            <div className="text-sm text-gray-600">Recite mantras related to your Nakshatra lord ({data.nakshatras.lord}) for spiritual growth.</div>
          </div>
        </div>
      </Card>

      {/* Disclaimer */}
      <Card className="p-4 bg-yellow-50 border-yellow-200">
        <p className="text-sm text-gray-700">
          <strong>Disclaimer:</strong> This Kundli is generated using Vedic astrology calculations. For detailed analysis and 
          personalized predictions, please consult a qualified astrologer. Astrological predictions should be used as guidance 
          and not as absolute certainties.
        </p>
      </Card>
    </div>
  );
}
